import React, { useState, useEffect } from 'react';
import api from '../api';

export default function ResearchCenter() {
  const [businessType, setBusinessType] = useState('');
  const [state,        setState]        = useState('');
  const [stateCode,    setStateCode]    = useState('');
  const [county,       setCounty]       = useState('');
  const [city,         setCity]         = useState('');
  const [allStates,    setAllStates]    = useState([]);
  const [stateData,    setStateData]    = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [loadingMsgIdx,setLoadingMsgIdx]= useState(0);
  const [error,        setError]        = useState('');
  const [report,       setReport]       = useState(null);
  const [history,      setHistory]      = useState([]);
  const [showHistory,  setShowHistory]  = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);

  const canUseResearchCenter = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      const role = stored?.role || '';
      if (role === 'administrator') return true; // platform superadmin bypass
      const cached = JSON.parse(localStorage.getItem('gpf_plan_features') || '{}');
      const workspace = JSON.parse(localStorage.getItem('gpf_workspace_cache') || '{}');
      const plan = workspace?.plan || 'free';
      return cached?.[plan]?.research_center === true;
    } catch { return false; }
  };

  const LOADING_MESSAGES = [
    "Researching your industry...",
    "Checking business density data...",
    "Looking up revenue and cost benchmarks...",
    "Compiling your report...",
  ];

  useEffect(() => {
    fetch('/us-locations/_states.json').then(r => r.json()).then(setAllStates).catch(() => {});
    api.get('/gpf/v1/ai-agent/research/history').then(res => setHistory(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!stateCode) { setStateData(null); return; }
    fetch(`/us-locations/${stateCode}.json`).then(r => r.json()).then(setStateData).catch(() => {});
  }, [stateCode]);

  const generateReport = async () => {
    if (!canUseResearchCenter()) { setShowUpgrade(true); return; }
    if (!businessType.trim()) { setError('Please describe the type of business.'); return; }
    if (!state) { setError('Please select a state.'); return; }
    setError(''); setLoading(true); setLoadingMsgIdx(0); setReport(null);

    const msgInterval = setInterval(() => {
      setLoadingMsgIdx(prev => Math.min(prev + 1, LOADING_MESSAGES.length - 1));
    }, 3000);

    try {
      const res = await api.post('/gpf/v1/ai-agent/research', { business_type: businessType, state, county, city });
      setReport(res.data);
      setHistory(prev => [res.data, ...prev]);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to generate report. Please try again.');
    } finally {
      clearInterval(msgInterval);
      setLoading(false);
    }
  };

  const viewHistoryItem = (item) => {
    setReport({ business_type: item.business_type, location: item.location, report: item.report });
    setShowHistory(false);
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>🔎 Research Center</h2>
          <p style={styles.sub}>Market data, competitor density, and financial benchmarks for your business idea</p>
        </div>
        {history.length > 0 && (
          <button style={styles.historyBtn} onClick={() => setShowHistory(!showHistory)}>
            📋 Past Reports ({history.length})
          </button>
        )}
      </div>

      {showHistory && (
        <div style={styles.historyPanel}>
          {history.map(item => (
            <button key={item.id} style={styles.historyItem} onClick={() => viewHistoryItem(item)}>
              <span style={{ fontWeight: 600 }}>{item.business_type}</span>
              <span style={{ color: '#94a3b8', fontSize: 12 }}> · {item.location} · {new Date(item.created_at).toLocaleDateString()}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div style={styles.errorBox}>⚠️ {error}</div>}

      <div style={styles.formCard}>
        <div style={styles.formGrid}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={styles.label}>Business Type</label>
            <input
              style={styles.input}
              value={businessType}
              onChange={e => setBusinessType(e.target.value)}
              placeholder="e.g. Italian restaurant, hair salon, coffee shop"
            />
          </div>
          <div>
            <label style={styles.label}>State *</label>
            <select style={styles.input} value={stateCode} onChange={e => {
              const code = e.target.value;
              const found = allStates.find(s => s.code === code);
              setStateCode(code); setState(found?.name || ''); setCounty(''); setCity('');
            }}>
              <option value="">Select state...</option>
              {allStates.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={styles.label}>County</label>
            {stateData ? (
              <select style={styles.input} value={county} onChange={e => { setCounty(e.target.value); setCity(''); }}>
                <option value="">Select county...</option>
                {Object.keys(stateData.counties).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input style={styles.input} value={county} onChange={e => setCounty(e.target.value)} placeholder="Select a state first" />
            )}
          </div>
          <div>
            <label style={styles.label}>City</label>
            {stateData && county && stateData.counties[county] ? (
              <select style={styles.input} value={city} onChange={e => setCity(e.target.value)}>
                <option value="">Select city...</option>
                {stateData.counties[county].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input style={styles.input} value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Pittsburgh" />
            )}
          </div>
        </div>
        <button style={styles.generateBtn} onClick={generateReport} disabled={loading}>
          {loading ? 'Researching...' : '🔎 Generate Research Report'}
        </button>
      </div>

      {loading && (
        <div style={styles.loadingBox}>
          <div style={styles.spinner} />
          <p style={{ fontSize: 14, fontWeight: 600, margin: '14px 0 4px' }}>{LOADING_MESSAGES[loadingMsgIdx]}</p>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>This can take up to a minute — please be patient.</p>
        </div>
      )}

      {report && !loading && (
        <div style={styles.reportCard}>
          <div style={styles.reportHeader}>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>{report.business_type}</h3>
            <p style={{ fontSize: 13, color: '#2563eb', margin: '4px 0 0' }}>📍 {report.location}</p>
          </div>

          <div style={styles.chartCard}>
            <p style={styles.sectionLabel}>📍 Similar Businesses Nearby</p>
            {(() => {
              const bc = report.report.business_count || {};
              const rows = [
                { label: 'City',   value: bc.city },
                { label: 'County', value: bc.county },
                { label: 'State',  value: bc.state },
              ];
              const max = Math.max(1, ...rows.map(r => typeof r.value === 'number' ? r.value : 0));
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {rows.map(r => (
                    <div key={r.label} style={styles.barRow}>
                      <span style={styles.barLabel}>{r.label}</span>
                      <div style={styles.barTrack}>
                        <div style={{
                          ...styles.barFill,
                          width: typeof r.value === 'number' ? `${Math.max(4, (r.value / max) * 100)}%` : '0%',
                        }} />
                      </div>
                      <span style={styles.barValue}>
                        {typeof r.value === 'number' ? r.value.toLocaleString() : 'N/A'}{r.label === 'City' && bc.city_capped ? '+' : ''}
                      </span>
                    </div>
                  ))}
                  {bc.city_source && (
                    <p style={styles.chartNote}>City count sourced from {bc.city_source} — real listed businesses, not an estimate.</p>
                  )}
                  {bc.note && <p style={styles.chartNote}>{bc.note}</p>}
                  {bc.city_listings?.length > 0 && (
                    <div style={styles.listingsBox}>
                      <p style={styles.listingsLabel}>Found in {report.location.split(',')[0] || 'this city'}:</p>
                      <div style={styles.listingsGrid}>
                        {bc.city_listings.map((l, i) => (
                          <div key={i} style={styles.listingRow}>
                            <span style={{ fontWeight: 600 }}>{l.name}</span>
                            {l.rating && <span style={styles.listingRating}>★ {l.rating}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div style={styles.chartCard}>
            <p style={styles.sectionLabel}>💰 Financial Benchmarks</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                { label: 'Average Annual Revenue', data: report.report.average_annual_revenue, color: '#2563eb' },
                { label: 'Startup Cost',            data: report.report.startup_cost_range,     color: '#d97706' },
                { label: 'Profit Margin',           data: report.report.profit_margin_range,     color: '#16a34a' },
              ].map(({ label, data, color }) => {
                if (!data || typeof data.low !== 'number' || typeof data.high !== 'number') {
                  return (
                    <div key={label}>
                      <p style={styles.rangeLabel}>{label}</p>
                      <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Data not available</p>
                    </div>
                  );
                }
                const scaleMax = data.high * 1.25;
                const lowPct   = (data.low / scaleMax) * 100;
                const highPct  = (data.high / scaleMax) * 100;
                return (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={styles.rangeLabel}>{label}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color }}>{data.display}</span>
                    </div>
                    <div style={styles.rangeTrack}>
                      <div style={{
                        ...styles.rangeFill,
                        left: `${lowPct}%`,
                        width: `${Math.max(3, highPct - lowPct)}%`,
                        background: color,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={styles.section}>
            <p style={styles.sectionLabel}>Market Segment</p>
            <p style={styles.sectionText}>{report.report.market_segment}</p>
          </div>

          <div style={styles.section}>
            <p style={styles.sectionLabel}>Growth Trend</p>
            <p style={styles.sectionText}>{report.report.growth_trend}</p>
          </div>

          <div style={styles.twoCol}>
            <div>
              <p style={styles.sectionLabel}>✓ Key Success Factors</p>
              <ul style={styles.list}>
                {(report.report.key_success_factors || []).map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
            <div>
              <p style={styles.sectionLabel}>⚠ Risks & Challenges</p>
              <ul style={styles.list}>
                {(report.report.risks_and_challenges || []).map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </div>

          {report.report.sources?.length > 0 && (
            <p style={styles.sources}>Sources: {report.report.sources.join(' · ')}</p>
          )}
          <p style={styles.disclaimer}>
            Figures are AI-researched estimates for planning purposes, not guarantees. Verify critical numbers with an accountant or industry association before making financial decisions.
          </p>
        </div>
      )}

      {showUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 36, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔎</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>Research Center Not Available</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
              Market research reports are included in the Business plan. Upgrade to unlock competitor density, revenue benchmarks, and more.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <a href="/workspace?upgrade=1" style={{ padding: '12px 24px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none', display: 'block' }}>
                Upgrade Plan →
              </a>
              <button
                style={{ padding: '11px 24px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
                onClick={() => setShowUpgrade(false)}
              >Maybe Later</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap:         { marginTop: 8 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 },
  title:        { fontSize: 19, fontWeight: 800, margin: 0 },
  sub:          { fontSize: 13, color: '#64748b', margin: '4px 0 0' },
  historyBtn:   { padding: '8px 16px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer' },
  historyPanel: { background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 10, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' },
  historyItem:  { textAlign: 'left', padding: '8px 10px', border: 'none', background: '#f8fafc', borderRadius: 6, fontSize: 13, cursor: 'pointer' },
  errorBox:     { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' },
  formCard:     { background: 'white', borderRadius: 12, padding: 22, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20 },
  formGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 },
  label:        { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:        { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  generateBtn:  { padding: '11px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  loadingBox:   { background: 'white', borderRadius: 12, padding: '30px 20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  spinner:      { width: 36, height: 36, border: '4px solid #eff6ff', borderTopColor: '#2563eb', borderRadius: '50%', margin: '0 auto', animation: 'gpf-spin 0.8s linear infinite' },
  reportCard:   { background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  reportHeader: { marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid #f1f5f9' },
  statGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 },
  statBox:      { background: '#f8fafc', borderRadius: 10, padding: '12px 14px' },
  statLabel:    { fontSize: 11, color: '#94a3b8', margin: '0 0 4px' },
  statValue:    { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  chartCard:    { background: '#f8fafc', borderRadius: 12, padding: 18, marginBottom: 16 },
  barRow:       { display: 'flex', alignItems: 'center', gap: 10 },
  barLabel:     { width: 52, fontSize: 13, color: '#64748b', flexShrink: 0 },
  barTrack:     { flex: 1, height: 20, background: '#e2e8f0', borderRadius: 6, overflow: 'hidden' },
  barFill:      { height: '100%', background: '#2563eb', borderRadius: 6, transition: 'width 0.4s ease' },
  barValue:     { width: 60, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#0f172a', flexShrink: 0 },
  chartNote:    { fontSize: 12, color: '#94a3b8', fontStyle: 'italic', margin: '4px 0 0' },
  rangeLabel:   { fontSize: 13, fontWeight: 600, color: '#374151' },
  rangeTrack:   { position: 'relative', height: 10, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' },
  rangeFill:    { position: 'absolute', top: 0, height: '100%', borderRadius: 99 },
  listingsBox:  { marginTop: 6, paddingTop: 12, borderTop: '1px solid #e2e8f0' },
  listingsLabel:{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '0 0 8px' },
  listingsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 },
  listingRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, background: 'white', padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0' },
  listingRating:{ color: '#d97706', fontSize: 11, fontWeight: 700, flexShrink: 0, marginLeft: 6 },
  section:      { marginBottom: 16 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' },
  sectionText:  { fontSize: 14, color: '#475569', lineHeight: 1.7, margin: 0 },
  twoCol:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 16 },
  list:         { fontSize: 13, color: '#475569', lineHeight: 1.8, paddingLeft: 18, margin: 0 },
  sources:      { fontSize: 12, color: '#94a3b8', margin: '10px 0 6px', paddingTop: 12, borderTop: '1px solid #f1f5f9' },
  disclaimer:   { fontSize: 11, color: '#94a3b8', fontStyle: 'italic', margin: 0 },
};

if (typeof document !== 'undefined' && !document.getElementById('gpf-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'gpf-spin-keyframes';
  style.textContent = '@keyframes gpf-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}